import React, { useEffect, useMemo, useState } from 'react';
import type { GenericItem } from '@marty-js/api-sdk/types';
import styled from 'styled-components';
import { AffiliateLink } from '../../atoms/link';
import slugify from '../../utils/slugify';
import { useLinkComponent } from '../../utils/component';
import { useTranslation } from '../../utils/translation';
import ComparativeCard from '../cards/service/comparative';
import PlanAlternativeCard from '../cards/service/plan-alternative';
import { ButtonIconReadMore } from '../../atoms/buttoniconReadMore';
import OnlineServiceCard from '../cards/service/online-service';

interface ClubicContainerProps {
  cell: any;
  SlateContent: React.FC<{ data: object }>;
  eventCta?: (shopName?: string, price?: string, position?: string, itemId?: number) => void;
}

const AllPlanVariants = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0 45px;
`;

// injection import { SlateContentPlugin } from '../slate.content'; dans props
const ServiceCardArticle = ({ cell, SlateContent, eventCta }: ClubicContainerProps) => {
  const { type, template } = cell.content.state;
  const [showAllItems, setShowAllItems] = useState<boolean>(false);
  const t = useTranslation();
  const NavLink = useLinkComponent();
  const [refererUrl, setRefererUrl] = useState<string>();

  useEffect(() => {
    setRefererUrl(slugify(document.title));
  }, []);

  const { conclusion, conclusionJson, imageId, alt, urlCta, urlTest, urlProduct, productVariant } =
    cell?.content?.state?.data ?? {};

  const mainCtaInfos = useMemo(
    () => cell?.content?.state?.data?.mainCtaInfos ?? {},
    [cell?.content?.state?.data?.mainCtaInfos],
  );

  mainCtaInfos.ctaPropsCard = useMemo(() => {
    if (mainCtaInfos?.link) {
      return {
        href: mainCtaInfos.link,
        openInNewWindow: !mainCtaInfos.internalLink,
        rel: mainCtaInfos.internalLink ? null : ['nofollow'],
        referer: refererUrl,
        onClick: () => eventCta(),
      };
    }

    return null;
  }, [eventCta, mainCtaInfos.internalLink, mainCtaInfos.link, refererUrl]);

  if (!cell.content.state.data) {
    return null;
  }

  const props = {
    rank: cell.content.state.rank,
    size: cell.size,
    image: {
      imageId,
      alt,
    },
    ctaLink: {
      href: urlCta,
      referer: refererUrl,
      as: AffiliateLink,
    },
    testLink: {
      href: urlTest,
      as: NavLink,
    },
    productLink: {
      href: urlProduct,
      as: NavLink,
    },
    customDisplays: {
      hasReviewLink:
        typeof cell.content.state?.isReviewLinkDisplayed === 'undefined'
          ? true
          : cell.content.state.isReviewLinkDisplayed,
      hasConclusion:
        typeof cell.content.state?.isConclusionDisplayed === 'undefined'
          ? true
          : cell.content.state.isConclusionDisplayed,
      hasProsCons:
        typeof cell.content.state?.isProsConsDisplayed === 'undefined' ? true : cell.content.state.isProsConsDisplayed,
      hasRating:
        typeof cell.content.state?.isRatingDisplayed === 'undefined' ? true : cell.content.state.isRatingDisplayed,
    },
    mainCtaInfos,
    ...cell.content.state.data,
  };

  let allPlanVariants;

  switch (type || template) {
    case 'comparative':
      props.conclusion = conclusionJson ? <SlateContent data={conclusionJson} /> : conclusion;

      return <ComparativeCard {...props} />;
    case 'planAllVariants':
      allPlanVariants = cell.content.state.data.variants?.map((variant: GenericItem) => {
        if (!variant.imageId) {
          variant.imageId = imageId;
        }

        return <PlanAlternativeCard key={variant.id} forceSmall={false} genericItem={variant} />;
      });

      return (
        <AllPlanVariants>
          {allPlanVariants?.slice(0, 3).map((item: any) => item)}
          {allPlanVariants?.length > 3 && showAllItems === false && (
            <ButtonIconReadMore onClick={() => setShowAllItems(true)}>
              {t('sdk.button.read_more_label')}
            </ButtonIconReadMore>
          )}
          {showAllItems && allPlanVariants.slice(3).map((item: any) => item)}
        </AllPlanVariants>
      );

    case 'onlineService':
      return <OnlineServiceCard {...props} />;
    case 'plan':
    default:
      return <PlanAlternativeCard forceSmall={false} genericItem={productVariant} />;
  }
};

export default ServiceCardArticle;
